
import { useState, type FormEvent } from 'react';

interface InquiryFormProps {
  selectedPlan: string;
}

export default function InquiryForm({ selectedPlan }: InquiryFormProps) {
  const [formData, setFormData] = useState({
    businessName: '',
    contactName: '',
    email: '',
    phone: '',
    website: '',
    category: '',
    plan: selectedPlan || '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >,
  ) => {
    const { name, value } = e.target;
    if (name === 'message' && value.length > 500) return;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');

    // Basic validation
    if (
      !formData.businessName ||
      !formData.contactName ||
      !formData.email ||
      !formData.phone ||
      !formData.category
    ) {
      setError('Please fill in all required fields.');
      return;
    }

    setIsSubmitting(true);

    try {
      const body = new URLSearchParams();
      body.append('businessName', formData.businessName);
      body.append('contactName', formData.contactName);
      body.append('email', formData.email);
      body.append('phone', formData.phone);
      body.append('website', formData.website);
      body.append('category', formData.category);
      body.append(
        'plan',
        formData.plan || selectedPlan || 'Not selected',
      );
      body.append('message', formData.message);

      const res = await fetch(
        'https://readdy.ai/api/form/d6a246dt1k61917cmgp0',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          body: body.toString(),
        },
      );

      if (res.ok) {
        setSubmitted(true);
      } else {
        // Try to extract a helpful message from the response
        let msg = 'Something went wrong. Please try again.';
        try {
          const data = await res.json();
          if (data?.error) msg = data.error;
        } catch {
          // ignore JSON parse errors
        }
        setError(msg);
      }
    } catch (err) {
      setError(
        'Network error. Please check your connection and try again.',
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <section
        id="inquiry"
        className="py-20 bg-gradient-to-b from-white to-amber-50/40"
      >
        <div className="max-w-2xl mx-auto px-6 text-center">
          <div className="w-20 h-20 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <i className="ri-check-double-line text-teal-600 text-3xl"></i>
          </div>
          <h2 className="text-3xl font-bold text-gray-900 mb-3">
            Inquiry Received!
          </h2>
          <p className="text-gray-600 mb-6">
            Thank you for your interest in advertising on Roseville.org.
            Our team will review your information and reach out within 1-2
            business days.
          </p>
          <button
            onClick={() => {
              setSubmitted(false);
              setFormData({
                businessName: '',
                contactName: '',
                email: '',
                phone: '',
                website: '',
                category: '',
                plan: '',
                message: '',
              });
            }}
            className="px-6 py-3 bg-teal-600 text-white rounded-lg font-semibold hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer"
          >
            Submit Another Inquiry
          </button>
        </div>
      </section>
    );
  }

  return (
    <section
      id="inquiry"
      className="py-20 bg-gradient-to-b from-white to-amber-50/40"
    >
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
          {/* Left Info */}
          <div>
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Ready to Get Started?
            </h2>
            <p className="text-gray-600 mb-8">
              Fill out the form and our advertising team will contact you
              within 1-2 business days to discuss the best options for your
              business.
            </p>

            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-teal-50 rounded-xl flex items-center justify-center flex-shrink-0">
                  <i className="ri-user-star-line text-teal-600 text-xl"></i>
                </div>
                <div>
                  <h4 className="font-bold text-gray-900 mb-1">
                    Dedicated Account Manager
                  </h4>
                  <p className="text-sm text-gray-600">
                    Get personalized support to maximize your advertising
                    results
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-teal-50 rounded-xl flex items-center justify-center flex-shrink-0">
                  <i className="ri-bar-chart-grouped-line text-teal-600 text-xl"></i>
                </div>
                <div>
                  <h4 className="font-bold text-gray-900 mb-1">
                    Performance Analytics
                  </h4>
                  <p className="text-sm text-gray-600">
                    Track views, clicks, and engagement with detailed reports
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-teal-50 rounded-xl flex items-center justify-center flex-shrink-0">
                  <i className="ri-shield-check-line text-teal-600 text-xl"></i>
                </div>
                <div>
                  <h4 className="font-bold text-gray-900 mb-1">
                    No Long-Term Contracts
                  </h4>
                  <p className="text-sm text-gray-600">
                    Month-to-month plans with the flexibility to cancel anytime
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-teal-50 rounded-xl flex items-center justify-center flex-shrink-0">
                  <i className="ri-community-line text-teal-600 text-xl"></i>
                </div>
                <div>
                  <h4 className="font-bold text-gray-900 mb-1">
                    Community Focused
                  </h4>
                  <p className="text-sm text-gray-600">
                    Reach engaged local residents who support small businesses
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Right Form */}
          <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100">
            <h3 className="text-xl font-bold text-gray-900 mb-6">
              Advertising Inquiry
            </h3>

            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 flex items-center gap-2">
                <i className="ri-error-warning-line"></i>
                {error}
              </div>
            )}

            <form
              id="business-advertising-inquiry"
              data-readdy-form
              onSubmit={handleSubmit}
              className="space-y-4"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Business Name *
                  </label>
                  <input
                    type="text"
                    name="businessName"
                    value={formData.businessName}
                    onChange={handleChange}
                    placeholder="Your Business Name"
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none transition-all"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Contact Name *
                  </label>
                  <input
                    type="text"
                    name="contactName"
                    value={formData.contactName}
                    onChange={handleChange}
                    placeholder="Your Full Name"
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none transition-all"
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email *
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="you@business.com"
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none transition-all"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Phone *
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="(916) 555-0000"
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none transition-all"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Website
                </label>
                <input
                  type="url"
                  name="website"
                  value={formData.website}
                  onChange={handleChange}
                  placeholder="https://yourbusiness.com"
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none transition-all"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Business Category *
                  </label>
                  <select
                    name="category"
                    value={formData.category}
                    onChange={handleChange}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none transition-all cursor-pointer"
                    required
                  >
                    <option value="">Select category</option>
                    <option value="Restaurant">Restaurant</option>
                    <option value="Retail &amp; Shopping">
                      Retail &amp; Shopping
                    </option>
                    <option value="Health &amp; Wellness">
                      Health &amp; Wellness
                    </option>
                    <option value="Home Services">Home Services</option>
                    <option value="Professional Services">
                      Professional Services
                    </option>
                    <option value="Automotive">Automotive</option>
                    <option value="Entertainment">Entertainment</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Preferred Plan
                  </label>
                  <select
                    name="plan"
                    value={formData.plan || selectedPlan}
                    onChange={handleChange}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none transition-all cursor-pointer"
                  >
                    <option value="">Select a plan</option>
                    <option value="Community Listing">
                      Community Listing — $49/mo
                    </option>
                    <option value="Featured Partner">
                      Featured Partner — $129/mo
                    </option>
                    <option value="Premium Sponsor">
                      Premium Sponsor — $249/mo
                    </option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Message{' '}
                  <span className="text-gray-400 font-normal">
                    ({formData.message.length}/500)
                  </span>
                </label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="Tell us about your business and advertising goals..."
                  rows={4}
                  maxLength={500}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none transition-all resize-none"
                ></textarea>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full py-3 bg-teal-600 text-white rounded-lg font-semibold hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isSubmitting ? (
                  <>
                    <i className="ri-loader-4-line animate-spin"></i>
                    Submitting...
                  </>
                ) : (
                  <>
                    <i className="ri-send-plane-line"></i>
                    Submit Inquiry
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
